const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const QUOTER_CACHE_SESSION_KEY = 'espp_quoter_cache_session_v1';
export const QUOTE_TYPES_CACHE_STORAGE_KEY = 'espp_quoter_quote_types_cache_v1';
export const SALE_TYPES_CACHE_STORAGE_KEY = 'espp_quoter_sale_types_cache_v1';
export const QUOTER_CACHE_STORAGE_KEYS = [
    QUOTE_TYPES_CACHE_STORAGE_KEY,
    SALE_TYPES_CACHE_STORAGE_KEY,
];

type CacheEntry<T> = {
    updatedAt: number;
    data: T;
};

let sessionLifecycleInitialized = false;

function safeRead<T>(key: string): Record<string, CacheEntry<T>> {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) {
            return {};
        }
        return JSON.parse(raw);
    } catch (_error) {
        return {};
    }
}

function safeWrite<T>(key: string, value: Record<string, CacheEntry<T>>): void {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (_error) {
        // Ignore storage write failures.
    }
}

function pruneExpiredEntries(storageKey: string): void {
    const store = safeRead<unknown>(storageKey);
    let hasChanges = false;
    Object.keys(store).forEach((entryKey) => {
        const entry = store[entryKey];
        const age = typeof entry?.updatedAt === 'number' ? Date.now() - entry.updatedAt : Infinity;
        if (!entry || age > ONE_DAY_MS) {
            delete store[entryKey];
            hasChanges = true;
        }
    });

    if (hasChanges) {
        safeWrite(storageKey, store);
    }
}

export function initEsppQuoterCacheSessionLifecycle(cacheKeys: string[]): void {
    if (sessionLifecycleInitialized) {
        return;
    }
    sessionLifecycleInitialized = true;

    try {
        const hasSession = !!sessionStorage.getItem(QUOTER_CACHE_SESSION_KEY);
        if (!hasSession) {
            clearEsppQuoterLocalCaches(cacheKeys);
        }
        cacheKeys.forEach((cacheKey) => pruneExpiredEntries(cacheKey));
        sessionStorage.setItem(QUOTER_CACHE_SESSION_KEY, String(Date.now()));
    } catch (_error) {
        // Ignore storage access issues.
    }
}

export function getEsppQuoterCachedValue<T>(storageKey: string, entryKey: string): T | null {
    const store = safeRead<T>(storageKey);
    const entry = store[entryKey];
    if (!entry) {
        return null;
    }

    const isExpired = typeof entry.updatedAt !== 'number' || Date.now() - entry.updatedAt > ONE_DAY_MS;
    if (isExpired) {
        delete store[entryKey];
        safeWrite(storageKey, store);
        return null;
    }

    return entry.data;
}

export function setEsppQuoterCachedValue<T>(storageKey: string, entryKey: string, data: T): void {
    const store = safeRead<T>(storageKey);
    store[entryKey] = {
        updatedAt: Date.now(),
        data,
    };
    safeWrite(storageKey, store);
}

export function clearEsppQuoterLocalCaches(cacheKeys: string[]): void {
    cacheKeys.forEach((key) => {
        try {
            localStorage.removeItem(key);
        } catch (_error) {
            // Ignore storage access issues.
        }
    });
}
